import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Menu, X } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { signOutAdmin } from '../lib/admin-auth';
import '../styles/admin-shared.css';

const nav = [
  ['Overview', '/admin'],
  ['Calendar', '/admin/calendar'],
  ['Bookings', '/admin/bookings'],
  ['Notifications', '/admin/notifications'],
  ['Enquiries', '/admin/enquiries'],
  ['Services', '/admin/services'],
  ['Staff', '/admin/staff'],
  ['Customers', '/admin/customers'],
  ['Content', '/admin/content'],
  ['Portfolio', '/admin/portfolio'],
  ['Availability', '/admin/availability'],
  ['Settings', '/admin/settings'],
] as const;

export default function AdminLayout({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  function closeMobileNav(restoreFocus = false) {
    setMobileNavOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => menuButtonRef.current?.focus());
    }
  }

  function signOut() {
    signOutAdmin();
    navigate('/admin/login', { replace: true });
  }

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;

    const previousOverflow = document.body.style.overflow;
    const desktopQuery = window.matchMedia('(min-width: 1001px)');
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus());
        return;
      }

      if (event.key !== 'Tab' || !sidebarRef.current) return;
      const focusableElements = Array.from(
        sidebarRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled])',
        ),
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements.at(-1);
      if (!firstElement || !lastElement) return;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    function handleDesktopChange(event: MediaQueryListEvent) {
      if (event.matches) setMobileNavOpen(false);
    }

    document.addEventListener('keydown', handleKeyDown);
    desktopQuery.addEventListener('change', handleDesktopChange);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      desktopQuery.removeEventListener('change', handleDesktopChange);
    };
  }, [mobileNavOpen]);

  return (
    <div className="studio-admin-shell">
      <header className="studio-admin-mobile-header">
        <div className="studio-admin-mobile-brand">
          <strong>MF_NAILEDIT</strong>
          <span>STUDIO ADMIN</span>
        </div>
        <button
          ref={menuButtonRef}
          className="studio-admin-menu-button"
          type="button"
          aria-label="Open admin navigation"
          aria-controls="studio-admin-navigation"
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen(true)}
        >
          <Menu aria-hidden="true" size={24} strokeWidth={1.8} />
        </button>
      </header>

      <aside
        ref={sidebarRef}
        id="studio-admin-navigation"
        className="studio-admin-sidebar"
        data-open={mobileNavOpen}
      >
        <div className="studio-admin-brand">
          <strong>MF_NAILEDIT</strong>
          <span>STUDIO ADMIN</span>
          <button
            ref={closeButtonRef}
            className="studio-admin-drawer-close"
            type="button"
            aria-label="Close admin navigation"
            onClick={() => closeMobileNav(true)}
          >
            <X aria-hidden="true" size={24} strokeWidth={1.8} />
          </button>
        </div>
        <nav aria-label="Admin navigation">
          {nav.map(([label, path]) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/admin'}
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={() => setMobileNavOpen(false)}
            >
              <i aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="studio-admin-account">
          <span>Authenticated administrator</span>
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      <button
        className="studio-admin-backdrop"
        data-open={mobileNavOpen}
        type="button"
        aria-label="Close admin navigation"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={() => closeMobileNav(true)}
      />

      <main className="studio-admin-main">
        <header className="studio-admin-header">
          <div>
            <h1>{title}</h1>
            <p>{description}</p>
          </div>
          {actions && (
            <div className="studio-admin-header-actions">{actions}</div>
          )}
        </header>
        {children}
      </main>
    </div>
  );
}
